import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Phone, MessageCircle, AlertTriangle, Clock, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminApi, errText, fmtDate } from "@/lib/api";
import { WHATSAPP_TEMPLATES, buildWhatsappLink } from "@/lib/whatsappTemplates";

const PHONE_DISPLAY = process.env.REACT_APP_PHONE_NUMBER;

const ReasonChip = ({ reason }) => {
  let Icon = Flame, color = "bg-red-100 text-red-700 border-red-200";
  if (reason.toLowerCase().includes("follow-up")) { Icon = Clock; color = "bg-amber-100 text-amber-700 border-amber-200"; }
  if (reason.toLowerCase().includes("expir")) { Icon = AlertTriangle; color = "bg-orange-100 text-orange-700 border-orange-200"; }
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-sm px-2 py-0.5 border ${color}`}>
      <Icon className="w-3 h-3" /> {reason}
    </span>
  );
};

export function CallsTodayTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tplPerLead, setTplPerLead] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/calls-today");
      setData(data);
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendWhatsapp = (lead) => {
    const tplId = tplPerLead[lead.id] || "welcome";
    const tpl = WHATSAPP_TEMPLATES.find((t) => t.id === tplId);
    if (!tpl) return;
    const url = buildWhatsappLink(lead.phone, tpl.build(lead));
    window.open(url, "_blank", "noopener");
  };

  const markStatus = async (lead, status) => {
    try {
      await adminApi.patch(`/admin/leads/${lead.id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  if (loading || !data) {
    return <div className="py-16 text-center text-[#71717A]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }

  const calls = data.calls || [];

  return (
    <div className="mt-6 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-[#0A0A0A]">Today's Calls</h2>
          <p className="mt-1 text-sm text-[#71717A]">
            {calls.length === 0 ? "No urgent calls — nice work 👏" : `${calls.length} customer${calls.length === 1 ? "" : "s"} to follow up`}
            {data.expiring_rewards_count > 0 && ` · ${data.expiring_rewards_count} coupon${data.expiring_rewards_count === 1 ? "" : "s"} expiring`}
          </p>
        </div>
        <Button onClick={load} variant="outline" className="h-9 text-xs border-[#E4E4E7]" data-testid="calls-today-refresh">
          Refresh
        </Button>
      </div>

      {calls.length === 0 && (
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-10 text-center">
          <p className="text-sm text-[#71717A]">Caught up. Check the Leads tab for the full pipeline.</p>
        </div>
      )}

      <div className="space-y-3" data-testid="calls-today-list">
        {calls.map((lead) => (
          <div
            key={lead.id}
            data-testid={`call-card-${lead.phone}`}
            className="bg-white border border-[#E4E4E7] rounded-sm p-5 hover:border-[#FF5A00] transition-colors"
          >
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex-1 min-w-[200px]">
                <p className="text-base font-bold text-[#0A0A0A]">{lead.name || "Unnamed"}</p>
                <p className="mt-0.5 text-xs text-[#52525B]">
                  📱 {lead.phone} · 🚗 {lead.car || "—"}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(lead.call_reasons || []).map((r, i) => <ReasonChip key={i} reason={r} />)}
                </div>
                {lead.coupon_code && (
                  <p className="mt-2 text-xs text-[#71717A]">
                    Coupon <span className="font-mono font-bold text-[#FF5A00]">{lead.coupon_code}</span> — {lead.prize} · expires {fmtDate(lead.coupon_expiry)}
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2">
                <Select
                  value={tplPerLead[lead.id] || "welcome"}
                  onValueChange={(v) => setTplPerLead({ ...tplPerLead, [lead.id]: v })}
                >
                  <SelectTrigger data-testid={`call-tpl-select-${lead.phone}`} className="w-[220px] h-9 text-xs border-[#E4E4E7] bg-white">
                    <SelectValue placeholder="Pick a message" />
                  </SelectTrigger>
                  <SelectContent>
                    {WHATSAPP_TEMPLATES.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid={`call-send-wa-${lead.phone}`}
                  onClick={() => sendWhatsapp(lead)}
                  className="h-9 bg-[#25D366] hover:bg-[#1fb457] text-white font-bold text-xs"
                >
                  <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Send
                </Button>
                <a
                  data-testid={`call-phone-${lead.phone}`}
                  href={`tel:+91${lead.phone}`}
                  className="inline-flex items-center justify-center bg-[#0A0A0A] hover:bg-[#1f1f1f] text-white text-xs font-bold rounded-sm h-9 px-3"
                >
                  <Phone className="w-3.5 h-3.5 mr-1.5" /> Call
                </a>
                <Select onValueChange={(v) => markStatus(lead, v)}>
                  <SelectTrigger data-testid={`call-status-${lead.phone}`} className="w-[120px] h-9 text-xs border-[#E4E4E7] bg-white">
                    <SelectValue placeholder="Update" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Contacted">Contacted</SelectItem>
                    <SelectItem value="Booked">Booked</SelectItem>
                    <SelectItem value="Visited">Visited</SelectItem>
                    <SelectItem value="Converted">Converted</SelectItem>
                    <SelectItem value="Lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
      </div>

      {PHONE_DISPLAY && (
        <p className="text-[11px] text-[#71717A] text-center pt-4">
          Calls open via tel: link · WhatsApp opens in a new tab · Status updates immediately
        </p>
      )}
    </div>
  );
}
